"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { useTranslations } from "next-intl";

import {
	FeaturedProduct,
	type FeaturedProductProps,
	FeaturedRecipe,
	type FeaturedRecipeProps
} from "@/composite/FeaturedProductRecipeCard";
import type { ProductsResponse } from "@/domain/product";
import type { ProductReference, RecipeReference, WithTheme } from "@/types";
import { resolveImageProps } from "@/app/utilities/images";
import { mapTagColor } from "@/app/utilities/mapTagColor";

import { FeaturedItem } from ".";

export interface FeaturedProductRecipeCardProps extends FeaturedItem {
	recipesData: RecipeReference[];
	productsData: ProductReference[];
	productsResponse: ProductsResponse;
}

/**
 * Featured Product/Recipe client block
 */
export function FeaturedProductRecipeCard({
	blockType,
	featured_tag,
	section_title,
	recipesData,
	productsData,
	productsResponse,
	theme
}: WithTheme<FeaturedProductRecipeCardProps>): JSX.Element {
	const t = useTranslations("featuredProductRecipeCard");
	const [tagColor, tagLabel] = featured_tag?.split("|") ?? ["purple", ""];

	if (productsResponse?.type === "failure") {
		throw new Error(productsResponse.message);
	}

	if (blockType === "featured_product") {
		const mainProduct = productsData?.[0];
		const productData = productsResponse?.products?.find(
			({ sku }) => sku === mainProduct?.sku
		);
		const showStrikethrough =
			productData?.salePrice !== productData?.listPrice;

		return (
			<FeaturedProduct<NextImageProps, NextLinkProps>
				mainProduct={{
					image: {
						...resolveImageProps("1:1"),
						component: NextImage,
						src: productData?.image ?? "",
						alt: productData?.name ?? ""
					},
					tag: {
						kind: "solid",
						size: "sm",
						color: mapTagColor(
							tagColor
						) as FeaturedProductProps["mainProduct"]["tag"]["color"],
						label: tagLabel
					},
					title: mainProduct.title,
					price: {
						display: productData?.salePrice,
						strikeThrough: showStrikethrough
							? productData?.listPrice
							: undefined
					},
					rating: {
						rating: productData?.starsRating ?? 0,
						label: productData?.numRatings
							? `(${productData?.numRatings})`
							: ""
					},
					description: mainProduct.meta_data.meta_description,
					cta: {
						component: NextLink,
						children: t("viewProduct"),
						href: mainProduct.meta_data.canonical_url.href
					}
				}}
				relatedRecipes={{
					title: section_title,
					recipeCards: recipesData.map((recipe) => ({
						image: {
							...resolveImageProps("1:1", recipe.image),
							component: NextImage
						},
						title: recipe.title,
						description: recipe.description,
						prepTime: t("prepCookTime", {
							prepTime: recipe.prep_time_in_minute,
							cookTime: recipe.cook_time_in_minute
						}),
						cta: {
							component: NextLink,
							children: t("viewRecipe"),
							href: recipe.seo.canonical_url.href
						}
					}))
				}}
				theme={theme}
			/>
		);
	}

	const mainRecipe = recipesData?.[0];

	return (
		<FeaturedRecipe
			mainRecipe={{
				image: {
					...resolveImageProps("1:1", mainRecipe.image),
					component: NextImage
				},
				tag: {
					kind: "solid",
					size: "sm",
					color: mapTagColor(
						tagColor
					) as FeaturedRecipeProps["mainRecipe"]["tag"]["color"],
					label: tagLabel
				},
				title: mainRecipe.title,
				description: mainRecipe.description,
				prepTime: t("prepCookTime", {
					prepTime: mainRecipe.prep_time_in_minute,
					cookTime: mainRecipe.cook_time_in_minute
				}),
				cta: {
					component: NextLink,
					children: t("viewRecipe"),
					href: mainRecipe.seo.canonical_url.href
				}
			}}
			relatedProducts={{
				title: section_title,
				productCards: productsData.map((product) => {
					const productData = productsResponse?.products?.find(
						({ sku }) => sku === product?.sku
					);
					const showStrikethrough =
						productData?.salePrice !== productData?.listPrice;

					return {
						image: {
							...resolveImageProps("1:1"),
							component: NextImage,
							src: productData?.image ?? "",
							alt: productData?.name ?? ""
						},
						title: product.title,
						price: {
							display: productData?.salePrice,
							strikeThrough: showStrikethrough
								? productData?.listPrice
								: undefined
						},
						rating: {
							rating: productData?.starsRating ?? 0,
							label: productData?.numRatings
								? `(${productData?.numRatings})`
								: ""
						},
						cta: {
							component: NextLink,
							children: t("viewProduct"),
							href: product.meta_data.canonical_url.href
						}
					};
				})
			}}
			theme={theme}
		/>
	);
}
